import { useCallback, useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { canRetainReadData } from '../../services/api/read-revalidation'
import type { ServerPatientFilter } from './patients-model'
import {
  patientReadError,
  type PatientsQuery,
  type PatientsRepository,
} from './patients-repository'

const PAGE_SIZE = 50

function directoryKey(
  tenantId: string,
  query: string,
  filter: ServerPatientFilter,
  cursor: string | null
) {
  return [
    'patients',
    tenantId,
    'directory',
    query,
    filter,
    cursor ?? 'first',
  ] as const
}

export function usePatients(
  repository: PatientsRepository,
  tenantId: string,
  options: {
    query?: string
    filter?: ServerPatientFilter
    cursor?: string | null
    prefetchNext?: boolean
  } = {}
) {
  const client = useQueryClient()
  const queryText = options.query?.trim() ?? ''
  const filter = options.filter ?? 'all'
  const cursor = options.cursor ?? null
  const request: PatientsQuery = {
    query: queryText,
    filter,
    cursor,
    pageSize: PAGE_SIZE,
  }
  const query = useQuery({
    queryKey: directoryKey(tenantId, queryText, filter, cursor),
    queryFn: () => repository.list(tenantId, request),
    enabled: Boolean(tenantId),
    staleTime: 30_000,
  })
  const nextCursor = query.data?.nextCursor
  useEffect(() => {
    if (
      options.prefetchNext === false ||
      !tenantId ||
      !nextCursor ||
      query.isFetching
    )
      return
    void client.prefetchQuery({
      queryKey: directoryKey(tenantId, queryText, filter, nextCursor),
      queryFn: () =>
        repository.list(tenantId, {
          query: queryText,
          filter,
          pageSize: PAGE_SIZE,
          cursor: nextCursor,
        }),
      staleTime: 30_000,
    })
  }, [
    client,
    filter,
    nextCursor,
    options.prefetchNext,
    query.isFetching,
    queryText,
    repository,
    tenantId,
  ])
  const refresh = useCallback(() => {
    void client.invalidateQueries(
      { queryKey: ['patients', tenantId, 'directory'] },
      { cancelRefetch: false }
    )
  }, [client, tenantId])
  const previousRepository = useRef(repository)
  useEffect(() => {
    if (previousRepository.current !== repository) refresh()
    previousRepository.current = repository
  }, [repository, refresh])
  const usable =
    query.data && (!query.isError || canRetainReadData(query.error))
  return {
    status: usable
      ? ('ready' as const)
      : query.isError
        ? patientReadError(query.error) === 'forbidden'
          ? ('forbidden' as const)
          : ('error' as const)
        : ('loading' as const),
    patients: usable ? query.data.patients : [],
    serviceIntents: usable ? query.data.serviceIntents : [],
    totalPatients: usable ? query.data.totalPatients : 0,
    startIndex: usable ? query.data.startIndex : 0,
    nextCursor: usable ? query.data.nextCursor : null,
    refreshing: query.isFetching && Boolean(query.data),
    refreshFailed: Boolean(usable && query.isError),
    dataUpdatedAt: query.dataUpdatedAt,
    refresh,
  }
}

export function usePatient(
  repository: PatientsRepository,
  tenantId: string,
  patientAuthorityId: string
) {
  const query = useQuery({
    queryKey: ['patients', tenantId, 'patient', patientAuthorityId],
    queryFn: () => repository.get(tenantId, patientAuthorityId),
    enabled: Boolean(tenantId && patientAuthorityId),
    staleTime: 30_000,
  })
  return {
    status: query.isError
      ? patientReadError(query.error) === 'forbidden'
        ? ('forbidden' as const)
        : ('error' as const)
      : query.data
        ? ('ready' as const)
        : ('loading' as const),
    patient: query.data?.patients[0] ?? null,
  }
}
